import { useState } from "react";
import { toast } from "react-toastify";

const Contact = () => {
  const [formData, setFormData] = useState({
    firstname: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });

  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setLoading(true);

    try {
      const response = await fetch(
        "http://localhost/bytecs-app/api/contact.php",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(formData),
        }
      );

      const result = await response.json();

      if (result.success) {
        toast.success(
          "Thank you! Your message has been sent successfully."
        );

        setFormData({
          firstname: "",
          email: "",
          phone: "",
          subject: "",
          message: "",
        });
      } else {
        toast.error(result.message || "Failed to send message.");
      }
    } catch (error) {
      console.error(error);
      toast.error("Server error. Please try again later.");
    }

    setLoading(false);
  };

  return (
    <section className="py-16 lg:py-24 relative overflow-hidden bg-white">
      {/* Background Effects */}
      <div className="absolute top-10 left-10 w-80 h-80 bg-cyan-100/30 rounded-full blur-3xl pointer-events-none -z-10"></div>
      <div className="absolute bottom-10 right-10 w-80 h-80 bg-blue-100/30 rounded-full blur-3xl pointer-events-none -z-10"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Heading */}
        <div className="text-center max-w-3xl mx-auto mb-20">
          <h1 className="text-4xl md:text-5xl font-extrabold text-slate-900">
            Contact Us
          </h1>

          <div className="h-1 w-12 bg-cyan-500 mx-auto mt-4 rounded-full"></div>

          <p className="mt-6 text-lg text-slate-600">
            Have questions or need assistance? Get in touch with our
            engineering or sales team today.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Contact Form */}
          <div className="bg-white p-8 lg:p-10 rounded-2xl border border-slate-200 shadow-lg lg:col-span-7">
            <h2 className="text-2xl font-bold text-slate-900 mb-6">
              Send Us a Message
            </h2>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Full Name
                  </label>

                  <input
                    type="text"
                    name="firstname"
                    value={formData.firstname}
                    onChange={handleChange}
                    required
                    placeholder="Enter your name"
                    className="w-full border border-slate-200 rounded-xl px-4 py-3 bg-slate-50 focus:ring-4 focus:ring-cyan-500/10 focus:border-cyan-500 outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Email Address
                  </label>

                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="Enter your email"
                    className="w-full border border-slate-200 rounded-xl px-4 py-3 bg-slate-50 focus:ring-4 focus:ring-cyan-500/10 focus:border-cyan-500 outline-none"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Phone Number
                  </label>

                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="Enter phone number"
                    className="w-full border border-slate-200 rounded-xl px-4 py-3 bg-slate-50 focus:ring-4 focus:ring-cyan-500/10 focus:border-cyan-500 outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Subject
                  </label>

                  <input
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    placeholder="Enter subject"
                    className="w-full border border-slate-200 rounded-xl px-4 py-3 bg-slate-50 focus:ring-4 focus:ring-cyan-500/10 focus:border-cyan-500 outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Message
                </label>

                <textarea
                  rows="5"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  placeholder="Write your message"
                  className="w-full border border-slate-200 rounded-xl px-4 py-3 bg-slate-50 focus:ring-4 focus:ring-cyan-500/10 focus:border-cyan-500 outline-none"
                ></textarea>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-cyan-600 text-white py-3.5 rounded-xl font-semibold hover:bg-cyan-700 transition duration-300 shadow-lg disabled:opacity-50"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                    Sending...
                  </span>
                ) : (
                  "Send Message"
                )}
              </button>
            </form>
          </div>

          {/* Contact Information */}
          <div className="lg:col-span-5 space-y-8">
            <div className="bg-white p-8 rounded-2xl border border-slate-200 shadow-lg">
              <h2 className="text-2xl font-bold text-slate-900 mb-6">
                Contact Information
              </h2>

              <div className="space-y-6">
                <div>
                  <h4 className="font-bold text-slate-800">📍 Address</h4>
                  <p className="text-slate-500 text-sm mt-1">
                    Bytecs Technologies Pvt. Ltd.
                    <br />
                    Office No.17, Snehal Apartments,
                    <br />
                    D Wing, 1st Floor,
                    <br />
                    Pune - 411041
                  </p>
                </div>

                <div>
                  <h4 className="font-bold text-slate-800">📞 Phone</h4>
                  <p className="text-slate-500 text-sm mt-1">
                    +91 9834587366
                    <br />
                    +91 8446353266
                  </p>
                </div>

                <div>
                  <h4 className="font-bold text-slate-800">✉️ Email</h4>
                  <p className="text-slate-500 text-sm mt-1">
                    info@bytecstechnologies.com
                    <br />
                    sales@bytecstechnologies.com
                  </p>
                </div>

                <div>
                  <h4 className="font-bold text-slate-800">🕒 Hours</h4>
                  <p className="text-slate-500 text-sm mt-1">
                    Monday - Saturday
                    <br />
                    9:00 AM - 6:00 PM
                  </p>
                </div>
              </div>
            </div>

            {/* Google Map */}
            <div className="bg-white p-2 rounded-2xl border border-slate-200 shadow-lg overflow-hidden">
              <iframe
                title="Bytecs Technologies Location"
                src="https://maps.google.com/maps?q=Pune&t=&z=13&ie=UTF8&iwloc=&output=embed"
                width="100%"
                height="320"
                style={{ border: 0 }}
                loading="lazy"
                className="rounded-xl"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;